<template>
  <div data-testid="pdf-elements-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="(e: any) => handleRowClick(e)"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import DataTable from '../../grid/DataTable.vue';

const { t } = useI18n();

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

interface PDFElementRow {
  name?: string;
  type?: string;
  data?: string;
  [key: string]: unknown;
}

const datatableRef = ref<DataTableInstance | null>(null);

const emit = defineEmits<{
  'edit': [elementName: string];
}>();

const endpoint = computed<string>(() => '/index.php/config/elementos_documentos_pdf/buscar_elementos');

const tableColumns = computed(() => {
  return [
    {
      data: 'name',
      title: t('Nome'),
      width: 200,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'type',
      title: t('Tipo'),
      width: 150,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'data',
      title: t('Dados'),
      width: 300,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams) => {
        const value = params.value;
        if (typeof value === 'string' && value) {
          // Show truncated base64 data
          const truncated = value.length > 50
            ? value.substring(0, 50) + '...'
            : value;
          return truncated;
        }
        return '';
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `pdf_elements_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('Exportar CSV')
}));

const loadData = (): void => {
  log('PDFElementsTable', 'loadData', 'Triggering data reload');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('PDFElementsTable', 'handleDataLoaded', 'Data loaded successfully', data?.length);
};

const handleDataError = (error: unknown): void => {
  log('PDFElementsTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar elementos'));
};

const handleRowClick = (rowData?: PDFElementRow): void => {
  log('PDFElementsTable', 'handleRowClick', 'Row clicked', rowData);
  if (rowData && rowData.name) {
    emit('edit', rowData.name);
  }
};

const handleEditClick = (elementName: string): void => {
  log('PDFElementsTable', 'handleEditClick', 'Edit clicked', elementName);
  if (elementName) {
    emit('edit', elementName);
  }
};

const handleRefresh = (): void => {
  log('PDFElementsTable', 'handleRefresh', 'Refresh event received');
  loadData();
};

onMounted(() => {
  log('PDFElementsTable', 'mounted', 'Component mounted');
  eventBus.on('pdf-elements.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  log('PDFElementsTable', 'beforeUnmount', 'Component unmounting');
  eventBus.off('pdf-elements.refresh', handleRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* No additional styles needed */
</style>
